import React from "react";
import { useSelector } from "react-redux";
import { useGlobalContext } from "context/global";
import clsx from "clsx/lite";
import extractIdFromGid from "helpers/extract-gid";
import useCartQuantityActions from "hooks/useCartQuantityActions";

const QuantityInput = ({ product, openCart = false, newLayout = false, onRemoved }) => {
	const { items, updating, checkoutUrl } = useSelector((state) => state.cart);
	const { isCollection } = useGlobalContext();
	const { addProduct, isFreeGift, updateQuantity } = useCartQuantityActions({ product, openCart, onRemoved });

	if (!product.variant) return null;

	const { variant } = product;
	const cartItem = items?.[variant.id];
	const showNewLayout = isCollection || newLayout;
	const gid = extractIdFromGid(variant?.id);

	if (!checkoutUrl) return <div className="h-[173px]" />;

	return (
		<div className="flex md:m-auto md:justify-center items-center">
			{!cartItem?.id ? (
				<AddToCartButton
					gid={gid}
					addProduct={addProduct}
					updating={updating}
					isFreeGift={isFreeGift}
					showNewLayout={showNewLayout}
				/>
			) : (
				<QuantityControlButtons
					gid={gid}
					quantity={cartItem.quantity}
					updateQuantity={updateQuantity}
					updating={updating}
					isFreeGift={isFreeGift}
					showNewLayout={showNewLayout}
					variantWeight={variant.weight}
				/>
			)}
		</div>
	);
};

const AddToCartButton = ({ gid, addProduct, updating, isFreeGift, showNewLayout }) => {
	if (isFreeGift) return null;

	return (
		<button
			onClick={addProduct}
			id={`add-to-cart-${gid}`}
			className={clsx(
				showNewLayout
					? "bg-beige rounded-lg md:w-full w-10/12 py-1 text-h3/5 md:text-3xl/5 mt-4"
					: "plus-minus-button bg-plus-btn"
			)}
			disabled={updating}
			aria-label="Quantity"
		>
			{showNewLayout ? "+" : ""}
		</button>
	);
};

const QuantityControlButtons = ({ gid, quantity, updateQuantity, updating, isFreeGift, showNewLayout, variantWeight }) => {
	const buttonClass = clsx(
		"plus-minus-button",
		showNewLayout ? "!w-3.5" : "!size-11 md:!w-14 md:!h-8 !rounded-[37px]"
	);

	return (
		<div
			className={clsx(
				"flex gap-4 items-center mt-4",
				variantWeight <= 1 && "justify-start",
				showNewLayout && "bg-brown w-10/12 md:w-full justify-between px-6 rounded-lg"
			)}
		>
			<button
				id={`minus-action-${gid}`}
				aria-label="Minus"
				disabled={updating}
				className={clsx(buttonClass, showNewLayout ? "bg-minus" : "bg-minus-white")}
				onClick={() => updateQuantity(-1)}
			/>
			<p className={clsx("text-base", showNewLayout ? "text-white" : "text-secondary")}>
				{quantity}
			</p>
			<button
				id={`plus-action-${gid}`}
				aria-label="Plus"
				disabled={updating}
				className={clsx(
					buttonClass,
					showNewLayout ? "bg-plus" : "bg-plus-black",
					isFreeGift && "opacity-20 pointer-events-none"
				)}
				onClick={() => updateQuantity(1)}
			/>
		</div>
	);
};

export default QuantityInput;
